import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PROJECT_STATUS } from "@/lib/taskUi";
import { useAuth } from "@/context/AuthContext";
import { ProjectBoard, UNCAT } from "@/components/projects/ProjectBoard";
import { ManageCategoriesDialog } from "@/components/projects/ManageCategoriesDialog";
import { ProjectCategorySelect } from "@/components/projects/ProjectCategorySelect";
import { FolderKanban, Plus, Tags } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const isCandidate = !!roles?.size && [...roles].every((r) => r === "candidate" || r === "referral_partner");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [canCreateCat, setCanCreateCat] = useState(false);  const [add, setAdd] = useState(null);
  const [manage, setManage] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const load = useCallback(() => {
    const params = status === "all" ? {} : { status };
    api.get("/projects", { params }).then((r) => setItems(r.data.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
    api.get("/project-categories").then((r) => { setCats(r.data.items || []); setCanCreateCat(!!r.data.can_create_shared); }).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (searchParams.get("create")) { setAdd({ category_id: null }); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);
  if (!items) return <PageLoader />;

  const shownCats = catFilter === "all" || catFilter === UNCAT ? cats : cats.filter((c) => c.id === catFilter);
  const known = new Set(cats.map((c) => c.id));
  const shown = items.filter((p) => catFilter === "all" || (catFilter === UNCAT ? !known.has(p.category_id) : p.category_id === catFilter));
  const board = catFilter === UNCAT ? [] : shownCats;

  return (
    <div>
      <PageHeader title="Projects" description="Team initiatives that group related tasks and milestones."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setManage(true)} data-testid="manage-categories-button"><Tags className="w-4 h-4 mr-1.5" />Manage Categories</Button>
          <Button className="bg-brand hover:bg-brand-strong" onClick={() => setAdd({ category_id: null })} data-testid="add-project-button"><Plus className="w-4 h-4 mr-1.5" /> New Project</Button>
        </div>} />
      {(
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="projects-filters">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-9 w-52 text-sm" data-testid="projects-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-64"><SelectItem value="all">All Categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}<SelectItem value={UNCAT}>Uncategorized</SelectItem></SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-44 text-sm" data-testid="projects-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All statuses</SelectItem>
              {Object.entries(PROJECT_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}<SelectItem value="archived">Archived</SelectItem></SelectContent>
          </Select>
          {status !== "all" && <span className="text-xs text-slate-400" data-testid="projects-drag-hint">Clear the status filter to drag projects into order.</span>}
        </div>
      )}
      {items.length ? (
        <ProjectBoard projects={shown} cats={board} canDrag={status === "all"} showEmpty={catFilter === "all" && status === "all"}
          onOpen={(p) => navigate(`/projects/${p.id}`)} onAdd={(cid) => setAdd({ category_id: cid })} onEditCategories={() => setManage(true)} onSaved={load} />
      ) : <EmptyState icon={FolderKanban} title="No projects yet" description="Create a project to organize a team initiative." />}
      {add && <CreateProject initialCategory={add.category_id} cats={cats} setCats={setCats} canCreateCat={canCreateCat} canShared={!isCandidate}
        onClose={() => setAdd(null)} onCreated={(p) => { setAdd(null); navigate(`/projects/${p.id}`); }} />}
      {manage && <ManageCategoriesDialog cats={cats} canCreate={canCreateCat} personalOnly={isCandidate} onClose={() => { setManage(false); load(); }} onChanged={() => {}} />}
    </div>
  );
}

function CreateProject({ initialCategory, cats, setCats, canCreateCat, canShared, onClose, onCreated }) {
  const initCat = cats.find((c) => c.id === initialCategory);
  const [form, setForm] = useState({ name: "", description: "", status: "planning", priority: "normal", target_date: "", team: [], owner_id: "",
    project_scope: initCat && !initCat.is_personal && canShared ? "shared" : "personal", category_id: initCat && (initCat.is_personal || canShared) ? initialCategory : null });
  const personal = form.project_scope === "personal";
  const catOptions = cats.filter((c) => !!c.is_personal === personal);
  const setScope = (s) => setForm((f) => ({ ...f, project_scope: s, category_id: null, team: [], owner_id: "" }));
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/tasks/assignable-users").then((r) => setUsers(r.data.items)).catch(() => {}); }, []);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    setSaving(true);
    try {
      const cat = cats.find((c) => c.id === form.category_id);
      const { data } = await api.post("/projects", { ...form, target_date: form.target_date || null, owner_id: form.owner_id || null, organization_id: personal ? null : cat?.organization_id || null });
      toast.success("Project created"); onCreated(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggleMember = (uid) => setForm((f) => ({ ...f, team: f.team.includes(uid) ? f.team.filter((x) => x !== uid) : [...f.team, uid] }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="create-project-dialog">
        <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto tr-scroll pr-1">
          <div><Label className="text-xs font-semibold">Project Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" placeholder="e.g. Open New Agency" /></div>
          <div><Label className="text-xs font-semibold">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description" /></div>
          {canShared && <div><Label className="text-xs font-semibold">Project Type</Label>
            <div className="flex flex-col gap-1.5 mt-1">
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={personal} onChange={() => setScope("personal")} data-testid="project-type-personal" />Personal Project <span className="text-xs text-slate-400">— private to you</span></label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={!personal} onChange={() => setScope("shared")} data-testid="project-type-shared" />Shared / Organizational Project</label>
            </div></div>}
          <div><Label className="text-xs font-semibold">Category</Label>
            <ProjectCategorySelect value={form.category_id} onChange={(v) => setForm({ ...form, category_id: v })} cats={catOptions} setCats={(next) => setCats([...cats, ...next.filter((n) => !cats.some((c) => c.id === n.id))])} canCreate={personal || canCreateCat} personal={personal} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="project-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(PROJECT_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Target Date</Label>
              <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="project-target-date" /></div>
          </div>
          {!personal && <><div><Label className="text-xs font-semibold">Owner</Label>
            <Select value={form.owner_id || "__me__"} onValueChange={(v) => setForm({ ...form, owner_id: v === "__me__" ? "" : v })}>
              <SelectTrigger data-testid="project-owner"><SelectValue placeholder="Me" /></SelectTrigger>
              <SelectContent className="bg-white max-h-56"><SelectItem value="__me__">Me</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold mb-1 block">Team Members</Label>
            <div className="flex flex-wrap gap-2">
              {users.map((u) => (
                <button key={u.user_id} onClick={() => toggleMember(u.user_id)} data-testid={`project-team-${u.user_id}`}
                  className={`text-xs rounded-full px-2.5 py-1 border ${form.team.includes(u.user_id) ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}>{u.name}</button>
              ))}
            </div></div></>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="project-submit">Create Project</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
